'use client';

import { createContext, ReactNode, useContext, useEffect } from "react";
import {
    useCollectionTypeEditor,
    UseCollectionTypeEditorType,
} from "@/app/lib/hooks/use-editor/useCollectionTypeEditor";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import {
    Exhibition,
    ExhibitionInput,
    PublicationStatus,
    UploadFile,
} from "@/app/lib/generated/graphql-types";
import { getSpecialExhibition } from "@/app/features/sites/special-exhibitions/data-access/queries";
import { updateSpecialExhibition } from "@/app/features/sites/special-exhibitions/data-access/mutations";
import { SpecialExhibitionItemSchema } from "@/app/features/sites/special-exhibitions/types/schema";
import type { SpecialExhibitionItemEditable } from "@/app/features/sites/special-exhibitions/types/types";
import { useSiteSpecialExhibitionsEditor } from "@/app/features/sites/special-exhibitions/contexts/SiteSpecialExhibitionsEditorContext";
import { getUploadFileId } from "@/app/lib/common/get-image-id";

const SpecialExhibitionItemEditorContext = createContext<
    UseCollectionTypeEditorType<SpecialExhibitionItemEditable> | undefined
>(undefined);

export function useSpecialExhibitionItemEditor() {
    const context = useContext(SpecialExhibitionItemEditorContext);
    if (!context) {
        throw new Error(
            "useSpecialExhibitionItemEditor must be used within SpecialExhibitionItemEditorProvider",
        );
    }
    return context;
}

function editableFromExhibition(exhibition: Exhibition): SpecialExhibitionItemEditable {
    return {
        documentId: exhibition.documentId,
        title: exhibition.title,
        subtitle: exhibition.subtitle,
        excerpt: exhibition.excerpt,
        startDate: exhibition.startDate ?? "",
        endDate: exhibition.endDate ?? "",
        isHighlight: exhibition.isHighlight,
        promoImage: exhibition.promoImage,
        flyerImage: exhibition.flyerImage,
    };
}

async function inputFromEditable(
    editable: SpecialExhibitionItemEditable,
): Promise<ExhibitionInput> {
    return {
        title: editable.title,
        subtitle: editable.subtitle,
        excerpt: editable.excerpt,
        startDate: editable.startDate,
        endDate: editable.endDate,
        isHighlight: editable.isHighlight,
        promoImage: await getUploadFileId(editable.promoImage as UploadFile),
        flyerImage: editable.flyerImage
            ? await getUploadFileId(editable.flyerImage as UploadFile)
            : null,
    };
}

export default function SpecialExhibitionItemEditorProvider({
    documentId,
    children,
}: {
    documentId: string;
    children: ReactNode;
}) {
    const { registerChildEditor, unregisterChildEditor } = useSiteSpecialExhibitionsEditor();
    const editor = useCollectionTypeEditor<
        Exhibition,
        SpecialExhibitionItemEditable,
        ExhibitionInput
    >({
        documentId,
        schema: SpecialExhibitionItemSchema,
        loadEntity: getSpecialExhibition,
        update: updateSpecialExhibition,
        toEditable: editableFromExhibition,
        toInput: inputFromEditable,
        manageDraftMode: false,
        autosave: async (targetDocumentId, data) => updateSpecialExhibition({
            documentId: targetDocumentId,
            data,
            status: PublicationStatus.Draft,
        }),
    });

    useEffect(() => {
        registerChildEditor(
            documentId,
            {
                hasChanges: editor.hasChanges,
                autosaveState: editor.autosaveState,
            },
            {
                publish: editor.publish,
                discardChanges: editor.discardChanges,
            },
        );

        return () => unregisterChildEditor(documentId);
    }, [
        documentId,
        editor.autosaveState,
        editor.discardChanges,
        editor.hasChanges,
        editor.publish,
        registerChildEditor,
        unregisterChildEditor,
    ]);

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SpecialExhibitionItemEditorContext.Provider value={editor}>
            {children}
        </SpecialExhibitionItemEditorContext.Provider>
    );
}
